"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronRight } from "lucide-react";
import Image from "next/image";
import arrow2 from "@/../public/assets/images/arrow2.svg";
import { headerSpring } from "./blockCard.constants";
import { ReorderSVG } from "@/components/ui/SVG";

type Props = {
  blockType: string;
  localeStrings: any;
  inputDir: "ltr" | "rtl";
  indexNumber: number;
  errors: string[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  showCheckbox: boolean;
  checked: boolean;
  disableCheckbox: boolean;
  onCheckChange?: (checked: boolean) => void;
  isDragging?: boolean;
  countLabel?: string;
  showReorderIcon?: boolean;
  dragHandleAttributes?: Record<string, any>;
  dragHandleListeners?: Record<string, any>;
};

export function BlockCardHeader({
  blockType,
  localeStrings,
  inputDir,
  indexNumber,
  errors,
  collapsed,
  onToggleCollapse,
  showCheckbox,
  checked,
  disableCheckbox,
  onCheckChange,
  isDragging = false,
  countLabel,
  showReorderIcon = false,
  dragHandleAttributes,
  dragHandleListeners,
}: Props) {
  const checkboxTransition = isDragging ? { duration: 0 } : headerSpring;
  const stopIfCheckbox = (e: React.SyntheticEvent) => {
    if (showReorderIcon) return;
    e.stopPropagation();
  };
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="relative flex min-h-8 items-center">
        <motion.div
          layout={isDragging ? false : "position"}
          className="relative flex items-center gap-2 rounded-md py-1"
          animate={
            showCheckbox
              ? inputDir === "rtl"
                ? { x: -10, marginRight: 32 }
                : { x: 10, marginLeft: 32 }
              : { x: 0, marginLeft: 0, marginRight: 0 }
          }
          transition={isDragging ? { duration: 0 } : headerSpring}
        >
          <span className="text-sm font-semibold capitalize text-slate-800">
            {localeStrings.blocks[blockType] ?? blockType}
          </span>
          <span className="pointer-events-none absolute bottom-1 ltr:-left-3 rtl:-right-3">
            <Image className="w-2 rtl:-rotate-90" src={arrow2} alt="arrow" />
          </span>
        </motion.div>

        <AnimatePresence>
          {showCheckbox ? (
            <motion.label
              key="block-checkbox"
              className={`pointer-events-auto absolute inset-y-0 flex items-center ${inputDir === "rtl" ? "right-0 pr-1" : "left-0 pl-1"}`}
              initial={{ x: inputDir === "rtl" ? 14 : -14, opacity: 0, scale: 0.9 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              exit={{ x: inputDir === "rtl" ? 14 : -14, opacity: 0, scale: 0.9 }}
              transition={checkboxTransition}
              onMouseDown={stopIfCheckbox}
              onPointerDown={stopIfCheckbox}
              onClick={stopIfCheckbox}
            >
              {showReorderIcon ? (
                <span
                  className="flex h-5 w-5 items-center justify-center text-slate-400"
                  style={{
                    touchAction: "none",
                    cursor: "grab",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                  }}
                  onPointerDown={(e) => {
                    // Prevent long-press text selection/callout from cancelling the drag.
                    e.preventDefault();
                  }}
                  {...(dragHandleAttributes ?? {})}
                  {...(dragHandleListeners ?? {})}
                >
                  <ReorderSVG className="h-4 w-4" />
                </span>
              ) : (
                <input
                  type="checkbox"
                  className="relative h-5 w-5 cursor-pointer appearance-none rounded border-2 border-pinkCustom bg-transparent transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 checked:border-pinkCustom before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:h-2.5 before:w-2.5 before:-translate-x-1/2 before:-translate-y-1/2 before:scale-0 before:rounded-xs before:bg-pinkCustom before:transition before:duration-300 checked:before:scale-100"
                  checked={checked}
                  disabled={disableCheckbox}
                  onChange={(e) => {
                    e.stopPropagation();
                    onCheckChange?.(e.target.checked);
                  }}
                />
              )}
            </motion.label>
          ) : null}
        </AnimatePresence>
      </div>
      {errors.length ? <span className="text-xs text-red-600">{errors[0]}</span> : null}
      <div className="flex items-center gap-2">
        {countLabel ? (
          <span className="text-xs text-slate-500">{countLabel}</span>
        ) : null}
        <button
          type="button"
          className="flex items-center gap-2 rounded px-1 py-0.5 text-left"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
        >
          {collapsed ? <ChevronRight className="h-4 w-4 text-slate-600 ltr:rotate-180" /> : <ChevronDown className="h-4 w-4 text-slate-600" />}
          <span className="flex h-6 min-w-6 items-center justify-center rounded bg-slate-100 px-2 text-xs font-semibold text-slate-700">
            {indexNumber}
          </span>
        </button>
      </div>
    </div>
  );
}
